import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { translate } from '@utils';
import { ExpandMore } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { getAllSubmissions } from '@services/submissionService';
import { setNotification } from '@actions/appActions';
import { useDispatch, useSelector } from 'react-redux';
import { currentSubmissionOriginSelector } from '@selectors/submission';
import { makeDeleteSubmissions } from '@actions/submissionActions';

import '../index.scss';

export interface Props {
  handleCancel: () => void;
  open: boolean;
  isDisabledConfirmationButton: boolean;
}

export const DeleteSampleModal = ({ handleCancel, open, isDisabledConfirmationButton }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [affectedSubmissions, setAffectedSubmissions] = useState('');
  const [deleteSubmissionsIds, setDeleteSubmissionsIds] = useState([]);
  const dispatch = useDispatch();
  const currentSubmissionOrigin = useSelector(currentSubmissionOriginSelector);

  const getAllSubmissionsByOriginId = async () => {
    try {
      const response = await getAllSubmissions({
        originId: currentSubmissionOrigin?.id,
      });
      const submissions = response.items;
      const submissionsIds = submissions.map((submission) => submission.id);
      setDeleteSubmissionsIds(submissionsIds);
      const affectedSubmissions = submissions
        .filter((submission) => !submission.isSample)
        .map((submission) => submission.title)
        .join(', ');
      if (!affectedSubmissions) {
        return setAffectedSubmissions(translate('no_templates_deleted'));
      }
      setAffectedSubmissions(affectedSubmissions);
    } catch (error) {
      dispatch(
        setNotification({
          textKey: 'list_of_affected_templates_error',
          variant: 'error',
        })
      );
      console.error('Error getting affected submissions:', error);
    }
  };

  const handleDeleteButton = () => {
    dispatch(
      makeDeleteSubmissions({
        submissionIds: deleteSubmissionsIds,
        isSample: true,
      })
    );
  };

  useEffect(() => {
    if (open && !affectedSubmissions) {
      getAllSubmissionsByOriginId();
    }
    if (!open && expanded) {
      setExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    setAffectedSubmissions('');
  }, [currentSubmissionOrigin?.id]);

  return (
    <Dialog
      className="delete-sample-modal"
      data-testid="delete-sample-modal"
      keepMounted
      open={open}
      onClose={handleCancel}
    >
      <DialogTitle data-testid="delete-sample-modal-title">
        {translate('delete_question_sample')}
      </DialogTitle>
      <DialogContent>
        <div className="delete-sample-modal__caution-text">{translate('caution_message')}</div>
        <div className="delete-sample-modal__content-text">
          {translate('delete_template_child')}
        </div>
        <div>
          <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-expanded={open}
              data-testid="delete-sample-modal-accordion"
            >
              <Typography>{translate('list_of_deleted_templates')}</Typography>
            </AccordionSummary>
            <AccordionDetails aria-expanded={open} data-testid="delete-sample-modal-details">
              {affectedSubmissions ? (
                <Typography>{affectedSubmissions}</Typography>
              ) : (
                <CircularProgress color="inherit" size={20} />
              )}
            </AccordionDetails>
          </Accordion>
        </div>
      </DialogContent>
      <DialogActions className="modal-actions">
        <Button
          className="actions-button delete"
          data-testid="button-delete-sample-modal"
          onClick={handleDeleteButton}
          disableRipple
          disabled={isDisabledConfirmationButton}
        >
          {translate('delete')}
        </Button>
        <Button
          className="actions-button cancel"
          data-testid="button-cancel-sample-modal"
          onClick={handleCancel}
          disableRipple
        >
          {translate('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
